import { useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { socketManager } from "../../context/Socket/SocketContext";
import isNativeApp from "../../utils/isNativeApp";

/**
 * useCapacitorResume
 * Native-only (iOS/Android) foreground catch-up bridge.
 *
 * The web realtime/reconnect machinery relies on DOM `visibilitychange` /
 * `focus` events + a 30s setInterval health check (SocketContext.js). In a
 * suspended native WebView (worst on iOS WKWebView) those DOM events are
 * unreliable/absent and setInterval is frozen — so a socket that died while the
 * app was backgrounded is never detected and the ticket list stays stale until
 * a full close+reopen.
 *
 * On the native `appStateChange` -> isActive (app returned to foreground):
 *   0. Short-flip skip: if the app was backgrounded for < 4s (share sheet,
 *      notification shade, quick app-switch peek) the socket is still alive
 *      and nothing was missed — skip entirely, so we never nuke the ticket
 *      list pagination / the open chat's scroll position for nothing.
 *   1. socketManager.forceReconnect() — IMMEDIATE (after the 300ms coalescing
 *      debounce): reconnects the dead socket, which re-joins rooms.
 *   2. ~1500ms later (deferred): queryClient.invalidateQueries(["tickets"])
 *      — refetch of the ticket list (staleTime:0) — plus the "app-resumed"
 *      dispatch that makes MessagesList backfill its page 1. Deferred so the
 *      backfill lands AFTER the reconnect + room-rejoin window instead of
 *      racing it (a fetch fired mid-reconnect can miss messages that arrive
 *      between the REST response and the room join).
 *
 * Web is a hard no-op (isNativeApp() gate) so nothing here affects the browser
 * build. Does NOT touch useTickets refetchOnWindowFocus (ghost-ticket trap).
 */
const RESUME_MIN_HIDDEN_MS = 4000;
const RESUME_BACKFILL_DELAY_MS = 1500;

const useCapacitorResume = () => {
  const queryClient = useQueryClient();
  // Timestamp of the last background transition (isActive === false).
  // Consulted on resume to detect short app-flips.
  const hiddenAtRef = useRef(null);

  useEffect(() => {
    if (!isNativeApp()) return; // web — do nothing

    let cancelled = false;
    let debounceTimer = null;
    let backfillTimer = null;
    let listenerHandle = null;

    const onResume = () => {
      // Short app-flips with a live socket must not nuke pagination/scroll:
      // nothing died in <4s of background, so a full resync would only reset
      // the ticket list to page 1 and yank the open chat to the bottom.
      if (
        hiddenAtRef.current &&
        Date.now() - hiddenAtRef.current < RESUME_MIN_HIDDEN_MS
      ) {
        hiddenAtRef.current = null;
        return;
      }
      hiddenAtRef.current = null;
      // Light debounce: appStateChange can fire in quick succession on some
      // devices; coalesce to a single catch-up.
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        try {
          socketManager.forceReconnect();
        } catch (e) {
          console.error("[Resume] forceReconnect failed:", e);
        }
        // Defer the list invalidate + chat backfill past the reconnect +
        // room-rejoin window (see header). A newer resume replaces a still-
        // pending backfill rather than stacking on it.
        if (backfillTimer) clearTimeout(backfillTimer);
        backfillTimer = setTimeout(() => {
          try {
            queryClient.invalidateQueries(["tickets"]);
          } catch (e) {
            console.error("[Resume] invalidateQueries failed:", e);
          }
          // The open conversation (MessagesList) is reducer-based, not react-query,
          // so invalidateQueries doesn't touch it, and its socket onConnect is a
          // one-shot that won't re-fire on forceReconnect. Signal it explicitly to
          // backfill messages missed while suspended.
          try {
            window.dispatchEvent(new Event("app-resumed"));
          } catch (e) {
            /* no-op */
          }
        }, RESUME_BACKFILL_DELAY_MS);
      }, 300);
    };

    const init = async () => {
      try {
        const capApp = await import("@capacitor/app");
        if (cancelled) return;
        // Capture THIS listener's handle and remove only it on cleanup — NOT
        // App.removeAllListeners(), which would also wipe the global back-button
        // / resume-splash listeners registered once in capacitorInit.
        listenerHandle = await capApp.App.addListener(
          "appStateChange",
          ({ isActive }) => {
            if (isActive) {
              onResume();
            } else {
              // Going to background: remember when, so the resume side can
              // tell a short flip from a real suspension.
              hiddenAtRef.current = Date.now();
            }
          }
        );
        if (cancelled && listenerHandle) {
          listenerHandle.remove();
          listenerHandle = null;
        }
      } catch (e) {
        console.error("[Resume] Failed to init @capacitor/app listener:", e);
      }
    };

    init();

    return () => {
      cancelled = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (backfillTimer) clearTimeout(backfillTimer);
      if (listenerHandle) {
        listenerHandle.remove();
        listenerHandle = null;
      }
    };
  }, [queryClient]);
};

export default useCapacitorResume;
